/**
 * Calque vectoriel du plan des pistes : pistes aux couleurs de balisage
 * liserées de blanc, pastilles numérotées, remontées en traits noirs avec
 * gares et noms, pictogrammes. Le survol et le clic remontent au plan.
 */
import { PICTO_LABEL, type LiftShape, type Overlay, type PisteShape } from './overlay';
import { PictoGlyph, PISTE_COLOR, WORKS_COLOR } from './style';

export type HoverKind = 'piste' | 'lift' | 'picto';

/** Ce qui est mis en avant : pistes et remontées actives (null : toutes). */
export interface Emphasis {
  pistes: Set<number> | null;
  lifts: Set<number> | null;
  hotPiste: number | null;
  hotLift: number | null;
}

interface Props {
  overlay: Overlay;
  w: number;
  h: number;
  emphasis: Emphasis;
  onHover: (kind: HoverKind | null, id: number | string, e?: MouseEvent) => void;
  onPick: (kind: 'piste' | 'lift' | 'building', id: number) => void;
}

function pisteClass(p: PisteShape, em: Emphasis): string {
  let c = `tm-piste tm-d-${p.difficulty}`;
  if (p.works) c += ' is-works';
  else if (!p.open) c += ' is-closed';
  if (em.pistes && !em.pistes.has(p.id)) c += ' is-off';
  if (em.hotPiste === p.id) c += ' is-hot';
  else if (em.hotPiste !== null) c += ' is-dim';
  return c;
}

function liftClass(l: LiftShape, em: Emphasis): string {
  let c = `tm-lift tm-k-${l.kind}`;
  if (l.status === 'construction') c += ' is-works';
  else if (l.status !== 'open') c += ' is-closed';
  if (em.lifts && !em.lifts.has(l.id)) c += ' is-off';
  if (em.hotLift === l.id) c += ' is-hot';
  return c;
}

export function MapLayer({ overlay, w, h, emphasis, onHover, onPick }: Props) {
  const { pistes, lifts, pictos } = overlay;
  return (
    <svg class="tm-svg" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-label="Tracés du domaine">
      <g class="tm-pistes">
        {pistes.map((p) =>
          p.d ? (
            <g
              key={p.id}
              class={pisteClass(p, emphasis)}
              onMouseEnter={(e) => onHover('piste', p.id, e)}
              onMouseMove={(e) => onHover('piste', p.id, e)}
              onMouseLeave={() => onHover(null, p.id)}
              onClick={() => onPick('piste', p.id)}
            >
              <path class="tm-halo" d={p.d} />
              <path class="tm-line" d={p.d} stroke={p.works ? WORKS_COLOR : PISTE_COLOR[p.difficulty]} />
              <path class="tm-hit" d={p.d} />
            </g>
          ) : null,
        )}
      </g>
      <g class="tm-lifts">
        {lifts.map((l) => (
          <g
            key={l.id}
            class={liftClass(l, emphasis)}
            onMouseEnter={(e) => onHover('lift', l.id, e)}
            onMouseMove={(e) => onHover('lift', l.id, e)}
            onMouseLeave={() => onHover(null, l.id)}
            onClick={() => onPick('lift', l.id)}
          >
            {l.d && <path class="tm-halo" d={l.d} />}
            {l.d && <path class="tm-cable" d={l.d} />}
            {l.marks && <path class="tm-marks" d={l.marks} />}
            {l.bottomSeen && <rect class="tm-station" x={l.bottom[0] - 3.5} y={l.bottom[1] - 3.5} width={7} height={7} />}
            {l.topSeen && <rect class="tm-station" x={l.top[0] - 3.5} y={l.top[1] - 3.5} width={7} height={7} />}
            {l.d && <path class="tm-hit" d={l.d} />}
          </g>
        ))}
      </g>
      <g class="tm-badges">
        {pistes.map((p) =>
          p.badge ? (
            <g
              key={p.id}
              class={`tm-badge ${pisteClass(p, emphasis)}`}
              transform={`translate(${p.badge[0].toFixed(1)} ${p.badge[1].toFixed(1)})`}
              onMouseEnter={(e) => onHover('piste', p.id, e)}
              onMouseLeave={() => onHover(null, p.id)}
              onClick={() => onPick('piste', p.id)}
            >
              <circle r={8.5} fill={p.works ? WORKS_COLOR : PISTE_COLOR[p.difficulty]} />
              <text y={3.6}>{p.num}</text>
            </g>
          ) : null,
        )}
      </g>
      <g class="tm-lift-labels">
        {lifts.map((l) =>
          l.label ? (
            <text key={l.id} class={`tm-lift-label ${liftClass(l, emphasis)}`} transform={`translate(${l.label.x.toFixed(1)} ${l.label.y.toFixed(1)}) rotate(${l.label.angle.toFixed(1)})`}>
              {l.name}
            </text>
          ) : null,
        )}
      </g>
      <g class="tm-pictos">
        {pictos.map((p) => (
          <g
            key={p.key}
            class="tm-picto"
            onMouseEnter={(e) => onHover('picto', p.key, e)}
            onMouseMove={(e) => onHover('picto', p.key, e)}
            onMouseLeave={() => onHover(null, p.key)}
            onClick={() => p.ids[0] !== undefined && onPick('building', p.ids[0])}
          >
            <title>{p.label || PICTO_LABEL[p.kind]}</title>
            {(Math.abs(p.x - p.ax) > 1 || Math.abs(p.y - p.ay) > 1) && <line class="tm-leader" x1={p.ax} y1={p.ay} x2={p.x} y2={p.y + 7} />}
            <circle class="tm-anchor" cx={p.ax} cy={p.ay} r={1.8} />
            <PictoGlyph kind={p.kind} x={p.x} y={p.y} />
            {p.ids.length > 1 && (
              <text class="tm-picto-count" x={p.x + 8} y={p.y - 5}>
                {p.ids.length}
              </text>
            )}
          </g>
        ))}
      </g>
    </svg>
  );
}
